export interface FileWithRelativePath {
  file: File;
  relativePath: string;
}

function readEntry(
  entry: FileSystemEntry,
  pathPrefix: string
): Promise<FileWithRelativePath[]> {
  return new Promise((resolve, reject) => {
    if (entry.isFile) {
      (entry as FileSystemFileEntry).file(
        (file) => resolve([{ file, relativePath: pathPrefix ? `${pathPrefix}/${file.name}` : file.name }]),
        (err) => reject(err)
      );
      return;
    }
    if (entry.isDirectory) {
      const dir = entry as FileSystemDirectoryEntry;
      const dirReader = dir.createReader();
      const dirPath = pathPrefix ? `${pathPrefix}/${entry.name}` : entry.name;

      const readAll = (): Promise<FileSystemEntry[]> => {
        return new Promise((res, rej) => {
          const acc: FileSystemEntry[] = [];
          const next = () => {
            dirReader.readEntries(
              (entries) => {
                if (entries.length === 0) {
                  res(acc);
                  return;
                }
                acc.push(...entries);
                next();
              },
              (err) => rej(err)
            );
          };
          next();
        });
      };

      readAll()
        .then((entries) => {
          return Promise.all(
            entries.map((child) => readEntry(child, dirPath))
          );
        })
        .then((nested) => resolve(nested.flat()))
        .catch(reject);
      return;
    }
    resolve([]);
  });
}

export interface GetFilesWithPathsResult {
  filesWithPaths: FileWithRelativePath[];
  /** Üst seviyede en az bir klasör sürüklendiyse true (progress kartı Folders alanında gösterilir) */
  hadFolder: boolean;
}

/**
 * DataTransfer'dan (sürükle-bırak) tüm dosyaları toplar.
 * Klasör bırakıldıysa iç içe yapı korunur; her dosya için relativePath üretilir.
 * Tarayıcı getAsEntry desteklemiyorsa sadece dosya adı kullanılır.
 * hadFolder: üst seviyede klasör varsa true (progress kartı Folders bölümünde gösterilir).
 */
export async function getFilesWithRelativePaths(
  dataTransfer: DataTransfer
): Promise<GetFilesWithPathsResult> {
  const results: FileWithRelativePath[] = [];
  let hadFolder = false;
  const items = dataTransfer.items;
  const supportsEntry = items.length > 0 && typeof (items[0] as any)?.webkitGetAsEntry === 'function';

  if (supportsEntry) {
    const entries: FileSystemEntry[] = [];
    for (let i = 0; i < items.length; i++) {
      const entry = (items[i] as any).webkitGetAsEntry?.();
      if (entry) {
        entries.push(entry);
        if (entry.isDirectory) hadFolder = true;
      }
    }
    for (const entry of entries) {
      const list = await readEntry(entry, '');
      results.push(...list);
    }
  }

  if (results.length === 0 && dataTransfer.files.length > 0) {
    for (let i = 0; i < dataTransfer.files.length; i++) {
      const file = dataTransfer.files[i];
      if (file && file instanceof File) {
        results.push({ file, relativePath: file.name });
      }
    }
  }

  return { filesWithPaths: results, hadFolder };
}
